import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useIntl } from "react-intl";
import { CheckCircle, Clock, CreditCard, Info, XCircle } from "lucide-react";
import StarRating from "components/ui/star-rating";
import { Button } from "components/ui/button";
import { cn } from "lib/utils";
import { buildRoute } from "routes";
import type { BookingInterface } from "types/booking";
import { BookingStatus } from "types/booking";
import { Role } from "types/user";
import {
    canCancelBooking as canCancelServiceBooking,
    formatDuration,
    formatEuroAmount,
    formatServiceHistoryDate,
    formatServiceHistoryTime,
    getBookingAmount,
    getFirstName,
    getFullName,
    getHourlyRateAmount,
    getSocialContributionsAmount,
    isCesuPlusActive,
} from "./format";
import { getStatusBadgeClassName, getStatusLabelId, isAutoValidatableStatus } from "./status";
import RateProviderDialog from "./RateProviderDialog";
import FinalizeAndDeclareDialog from "./FinalizeAndDeclareDialog";
import CancelBookingDialog from "./CancelBookingDialog";
import { useServiceTitle } from "./useServiceTitle";

type ServiceHistoryRowProps = {
    booking: BookingInterface;
    currentRole: Role.CLIENT | Role.PROVIDER;
};

const ServiceHistoryAmount: React.FC<{ amount: string; showCreditCardIcon?: boolean }> = ({
    amount,
    showCreditCardIcon = false,
}) => (
    <div
        data-testid="service-history-amount"
        className="flex min-w-16 items-center justify-end gap-1.5 text-sm font-semibold leading-5 tabular-nums text-slate-900"
    >
        {showCreditCardIcon ? <CreditCard className="h-4 w-4 shrink-0 text-slate-500" aria-hidden /> : null}
        {amount}
    </div>
);

const ServiceHistoryRow: React.FC<ServiceHistoryRowProps> = ({ booking, currentRole }) => {
    const intl = useIntl();
    const [isRatingOpen, setIsRatingOpen] = useState(false);
    const [isFinalizeOpen, setIsFinalizeOpen] = useState(false);
    const [isCancelOpen, setIsCancelOpen] = useState(false);
    const serviceTitle = useServiceTitle(booking);
    const isClosed = booking.status === BookingStatus.CLOSED;
    const isCancelled = booking.status === BookingStatus.CANCELLED;
    const canFinalizeAndDeclare = currentRole === Role.CLIENT && booking.status === BookingStatus.TO_VALIDATE;
    const canCancelBooking = canCancelServiceBooking(booking);
    const interlocutor = currentRole === Role.PROVIDER ? booking.client : booking.provider;
    const providerNote = booking.notes?.provider;
    const showProviderRating = isClosed && Boolean(providerNote);
    const canRateProvider = currentRole === Role.CLIENT && isClosed && !providerNote;
    const statusLabelId = getStatusLabelId(booking.status);
    const hourlyRateAmount = getHourlyRateAmount(booking);
    const socialContributionsAmount = getSocialContributionsAmount(booking);
    const shouldShowSocialContributions = currentRole === Role.CLIENT;
    const isProvider = currentRole === Role.PROVIDER;
    const serviceAddress = booking.client_address || booking.client?.address;

    const handleRateProvider = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setIsRatingOpen(true);
    };

    const handleFinalizeAndDeclare = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setIsFinalizeOpen(true);
    };

    const handleCancelBooking = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setIsCancelOpen(true);
    };

    const StatusIcon = isClosed ? CheckCircle : (isCancelled ? XCircle : Clock);
    const amount = formatEuroAmount(getBookingAmount(booking, currentRole));
    const cesuPlusActive = isCesuPlusActive(booking);
    const showCreditCardIcon = currentRole === Role.CLIENT;
    const showAutoValidateNotice = isAutoValidatableStatus(booking.status);

    return (
        <>
        <article className="relative rounded-[10px] border border-slate-200 bg-white transition-colors hover:bg-slate-50">
            <Link
                to={buildRoute.serviceHistoryDetail(booking.id)}
                className="absolute inset-0 z-[1] cursor-pointer rounded-[10px]"
                aria-label={intl.formatMessage(
                    { id: "profile.service_history.details.open" },
                    { title: serviceTitle },
                )}
            />
            {cesuPlusActive && currentRole === Role.CLIENT && (
                <span
                    data-testid="service-history-cesu-plus-badge"
                    className="pointer-events-none absolute right-0 top-0 z-[2] rounded-bl-[8px] rounded-tr-[10px] border-b border-l border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-bold leading-4 text-indigo-700"
                >
                    {intl.formatMessage({ id: "profile.service_history.details.cesu_plus" })}
                </span>
            )}
            <div
                data-testid="service-history-row"
                className="pointer-events-none relative z-[2] flex min-h-20 flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center"
            >
                <div
                    className={cn(
                        "flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px]",
                        isClosed ? "bg-emerald-50 text-emerald-600" : isCancelled ? "bg-red-50 text-red-600" : "bg-violet-100 text-indigo-600",
                    )}
                >
                    <StatusIcon className="h-5 w-5" aria-hidden />
                </div>

                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate text-sm font-medium leading-5 text-slate-900">{serviceTitle}</h3>
                    </div>
                    <p className="mt-1 text-xs leading-4 text-slate-600">
                        {getFullName(interlocutor)}
                        <span className="mx-3 text-slate-600">•</span>
                        {booking.start_time
                            ? intl.formatMessage(
                                  { id: "profile.service_history.date_time" },
                                  {
                                      date: formatServiceHistoryDate(booking.date),
                                      time: formatServiceHistoryTime(booking.start_time),
                                  },
                              )
                            : formatServiceHistoryDate(booking.date)}
                        <span className="mx-3 text-slate-600">•</span>
                        {formatDuration(booking.nb_hours)}
                        {isProvider ? (
                            serviceAddress ? (
                                <>
                                    <span className="mx-3 text-slate-600">•</span>
                                    <span className="truncate">{serviceAddress}</span>
                                </>
                            ) : null
                        ) : (
                            <>
                                <span className="mx-3 text-slate-600">•</span>
                                {intl.formatMessage(
                                    { id: "profile.service_history.hourly_rate" },
                                    { amount: formatEuroAmount(hourlyRateAmount) },
                                )}
                                {shouldShowSocialContributions ? (
                                    <>
                                        <span className="mx-3 text-slate-600">•</span>
                                        {intl.formatMessage(
                                            { id: "profile.service_history.social_contributions" },
                                            { amount: formatEuroAmount(socialContributionsAmount) },
                                        )}
                                    </>
                                ) : null}
                            </>
                        )}
                    </p>
                    {showAutoValidateNotice ? (
                        <p
                            data-testid="service-history-auto-validate-notice"
                            className="mt-1.5 flex items-start gap-1.5 text-xs leading-4 text-slate-400"
                        >
                            <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
                            <span>
                                {intl.formatMessage({ id: "profile.service_history.auto_validate_notice" })}
                            </span>
                        </p>
                    ) : null}
                </div>

                <div className="flex shrink-0 flex-wrap items-center gap-5 sm:min-w-[282px] sm:justify-end">
                    {showProviderRating && providerNote ? (
                        <div className="flex flex-col items-end gap-2">
                            <ServiceHistoryAmount amount={amount} showCreditCardIcon={showCreditCardIcon} />
                            <StarRating
                                value={providerNote.note}
                                starClassName="h-4 w-4"
                                emptyStarClassName="fill-transparent stroke-slate-300"
                                filledStarClassName="fill-amber-500 stroke-amber-500"
                                className="shrink-0"
                            />
                        </div>
                    ) : (
                        <>
                            {statusLabelId ? (
                                <span
                                    className={cn(
                                        "inline-flex h-6 w-20 items-center justify-center rounded-[10px] border text-xs font-medium leading-4",
                                        getStatusBadgeClassName(booking.status),
                                    )}
                                >
                                    {intl.formatMessage({ id: statusLabelId })}
                                </span>
                            ) : null}
                            {canCancelBooking ? (
                                <Button
                                    type="button"
                                    variant="destructive"
                                    onClick={handleCancelBooking}
                                    size="sm"
                                    className="pointer-events-auto relative z-[3] rounded-[10px] px-4 py-2 text-xs font-semibold"
                                >
                                    {intl.formatMessage({ id: "profile.service_history.cancel" })}
                                </Button>
                            ) : null}
                            {canFinalizeAndDeclare ? (
                                <Button
                                    type="button"
                                    onClick={handleFinalizeAndDeclare}
                                    size="sm"
                                    className="pointer-events-auto relative z-[3] rounded-[10px] px-4 py-2 text-xs font-extrabold shadow-[0_8px_18px_rgba(76,76,244,0.28)]"
                                >
                                    {intl.formatMessage({ id: "profile.service_history.finalize_modal.title" })}
                                </Button>
                            ) : null}
                            {canRateProvider ? (
                                <Button
                                    type="button"
                                    onClick={handleRateProvider}
                                    size="none"
                                    className="pointer-events-auto relative z-[3] rounded-[10px] px-11 py-3 text-base font-extrabold leading-6 shadow-[0_8px_18px_rgba(76,76,244,0.28)]"
                                >
                                    {intl.formatMessage(
                                        { id: "profile.service_history.rate" },
                                        { firstName: getFirstName(booking.provider) },
                                    )}
                                </Button>
                            ) : null}
                            <ServiceHistoryAmount amount={amount} showCreditCardIcon={showCreditCardIcon} />
                        </>
                    )}
                </div>
            </div>
        </article>
        {canRateProvider ? (
            <RateProviderDialog
                booking={booking}
                open={isRatingOpen}
                onOpenChange={setIsRatingOpen}
            />
        ) : null}
        {canFinalizeAndDeclare ? (
            <FinalizeAndDeclareDialog booking={booking} open={isFinalizeOpen} onOpenChange={setIsFinalizeOpen} />
        ) : null}
        {canCancelBooking ? (
            <CancelBookingDialog
                booking={booking}
                currentRole={currentRole}
                open={isCancelOpen}
                onOpenChange={setIsCancelOpen}
            />
        ) : null}
        </>
    );
};

export default ServiceHistoryRow;
